import axios, { AxiosError } from 'axios'
import type {
  AuthResponse,
  Library,
  LibraryWithOwnership,
  LibraryShare,
  Artist,
  ArtistWithAlbums,
  AlbumWithArtist,
  AlbumWithTracks,
  Track,
  TrackWithDetails,
  Video,
  SearchResult,
  Playlist,
  PlaylistWithTracks,
  SeriesWithEpisodes,
  SeriesDetail,
} from '../types'

const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// Auth
export const auth = {
  login: async (username: string, password: string): Promise<AuthResponse> => {
    const { data } = await api.post('/auth/login', { username, password })
    return data
  },
  register: async (username: string, password: string): Promise<AuthResponse> => {
    const { data } = await api.post('/auth/register', { username, password })
    return data
  },
  me: async () => {
    const { data } = await api.get('/auth/me')
    return data
  },
  changePassword: async (currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword })
    return data
  },
  createLinkCode: async (): Promise<{ code: string }> => {
    const { data } = await api.post('/auth/link-code')
    return data
  },
}

// Libraries
export const libraries = {
  list: async (): Promise<LibraryWithOwnership[]> => {
    const { data } = await api.get('/libraries')
    return data
  },
  listMine: async (): Promise<Library[]> => {
    const { data } = await api.get('/libraries/mine')
    return data
  },
  listShared: async (): Promise<Library[]> => {
    const { data } = await api.get('/libraries/shared')
    return data
  },
  listGlobal: async (): Promise<Library[]> => {
    const { data } = await api.get('/libraries/global')
    return data
  },
  get: async (id: string): Promise<Library> => {
    const { data } = await api.get(`/libraries/${id}`)
    return data
  },
  create: async (name: string, path: string | null, library_type: string, is_upload_library = false): Promise<Library> => {
    const { data } = await api.post('/libraries', { name, path, library_type, is_upload_library })
    return data
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/libraries/${id}`)
  },
  scan: async (id: string): Promise<void> => {
    await api.post(`/libraries/${id}/scan`)
  },
  rescan: async (id: string): Promise<{ message: string; library_id: string }> => {
    const { data } = await api.post(`/libraries/${id}/rescan`)
    return data
  },
  // Sharing
  share: async (libraryId: string, userId: string, accessLevel: string): Promise<void> => {
    await api.post(`/libraries/${libraryId}/share`, { user_id: userId, access_level: accessLevel })
  },
  unshare: async (libraryId: string, userId: string): Promise<void> => {
    await api.delete(`/libraries/${libraryId}/share/${userId}`)
  },
  getShares: async (libraryId: string): Promise<LibraryShare[]> => {
    const { data } = await api.get(`/libraries/${libraryId}/shares`)
    return data
  },
  setGlobal: async (libraryId: string, isGlobal: boolean): Promise<void> => {
    await api.put(`/libraries/${libraryId}/global`, { is_global: isGlobal })
  },
  setUploadTarget: async (libraryId: string, isUploadTarget: boolean): Promise<void> => {
    await api.put(`/libraries/${libraryId}/upload-target`, { is_upload_target: isUploadTarget })
  },
}

// Upload preview response type
export interface AudioPreviewResponse {
  filename: string
  title: string | null
  artist: string | null
  album: string | null
  track_number: number | null
  year: number | null
  genre: string | null
  duration_ms: number | null
  bitrate: number | null
  sample_rate: number | null
  file_format: string
  file_size: number
}

// Upload
export const upload = {
  getUploadLibrary: async (type: 'music' | 'movies' | 'tv'): Promise<Library> => {
    const { data } = await api.get('/upload/library', { params: { type } })
    return data
  },
  previewTrack: async (file: File): Promise<AudioPreviewResponse> => {
    const formData = new FormData()
    formData.append('file', file)
    const { data } = await api.post('/upload/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data
  },
  uploadTrack: async (
    libraryId: string,
    file: File,
    onProgress?: (percent: number) => void
  ): Promise<Track> => {
    const formData = new FormData()
    formData.append('file', file)
    const { data } = await api.post(`/upload/track/${libraryId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded * 100) / e.total))
        }
      },
    })
    return data
  },
  uploadVideo: async (
    libraryId: string,
    file: File,
    onProgress?: (percent: number) => void
  ): Promise<Video> => {
    const formData = new FormData()
    formData.append('file', file)
    const { data } = await api.post(`/upload/video/${libraryId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded * 100) / e.total))
        }
      },
    })
    return data
  },
  deleteUpload: async (libraryId: string, mediaType: 'track' | 'video', mediaId: string): Promise<void> => {
    await api.delete(`/upload/${libraryId}/${mediaType}/${mediaId}`)
  },
}

// Artists
export const artists = {
  list: async (limit = 50, offset = 0): Promise<Artist[]> => {
    const { data } = await api.get('/artists', { params: { limit, offset } })
    return data
  },
  browse: async (limit = 50, offset = 0): Promise<Artist[]> => {
    const { data } = await api.get('/artists/browse', { params: { limit, offset } })
    return data
  },
  get: async (id: string): Promise<ArtistWithAlbums> => {
    const { data } = await api.get(`/artists/${id}`)
    return data
  },
  tracks: async (id: string): Promise<TrackWithDetails[]> => {
    const { data } = await api.get(`/artists/${id}/tracks`)
    return data
  },
  merge: async (sourceId: string, targetId: string): Promise<{ albums_moved: number; tracks_moved: number }> => {
    const { data } = await api.post('/artists/merge', { source_id: sourceId, target_id: targetId })
    return data
  },
  refreshAll: async (): Promise<{ updated: number; total: number }> => {
    const { data } = await api.post('/artists/refresh-all')
    return data
  },
}

// Albums
export const albums = {
  list: async (limit = 50, offset = 0): Promise<AlbumWithArtist[]> => {
    const { data } = await api.get('/albums', { params: { limit, offset } })
    return data
  },
  browse: async (limit = 50, offset = 0): Promise<AlbumWithArtist[]> => {
    const { data } = await api.get('/albums/browse', { params: { limit, offset } })
    return data
  },
  get: async (id: string): Promise<AlbumWithTracks> => {
    const { data } = await api.get(`/albums/${id}`)
    return data
  },
  merge: async (sourceId: string, targetId: string): Promise<{ tracks_moved: number }> => {
    const { data } = await api.post('/albums/merge', { source_id: sourceId, target_id: targetId })
    return data
  },
  // Collection management (per-user library)
  addToCollection: async (id: string): Promise<{ success: boolean; in_collection: boolean }> => {
    const { data } = await api.post(`/albums/${id}/collection`)
    return data
  },
  removeFromCollection: async (id: string): Promise<{ success: boolean; in_collection: boolean }> => {
    const { data } = await api.delete(`/albums/${id}/collection`)
    return data
  },
  getCollectionStatus: async (id: string): Promise<{ in_collection: boolean }> => {
    const { data } = await api.get(`/albums/${id}/collection`)
    return data
  },
}

// Collection (per-user library management)
export const collection = {
  getStatusBatch: async (albumIds: string[]): Promise<Record<string, boolean>> => {
    const { data } = await api.get('/collection/albums/status', { params: { ids: albumIds.join(',') } })
    return data
  },
}

// Lyrics response type
export interface LyricsResult {
  plain_lyrics: string | null
  synced_lyrics: string | null
  instrumental: boolean
}

// Tracks
export const tracks = {
  get: async (id: string) => {
    const { data } = await api.get(`/tracks/${id}`)
    return data
  },
  recent: async (limit = 50): Promise<TrackWithDetails[]> => {
    const { data } = await api.get('/tracks/recent', { params: { limit } })
    return data
  },
  liked: async (limit = 100, offset = 0): Promise<TrackWithDetails[]> => {
    const { data } = await api.get('/tracks/liked', { params: { limit, offset } })
    return data
  },
  likedUnplayed: async (limit = 100, offset = 0): Promise<TrackWithDetails[]> => {
    const { data } = await api.get('/tracks/liked-unplayed', { params: { limit, offset } })
    return data
  },
  recentlyAdded: async (limit = 100, offset = 0): Promise<TrackWithDetails[]> => {
    const { data } = await api.get('/tracks/recently-added', { params: { limit, offset } })
    return data
  },
  all: async (limit = 10000, offset = 0): Promise<TrackWithDetails[]> => {
    const { data } = await api.get('/tracks/all', { params: { limit, offset } })
    return data
  },
  allStats: async (): Promise<{ count: number; total_duration_ms: number }> => {
    const { data } = await api.get('/tracks/all/stats')
    return data
  },
  likedStats: async (): Promise<{ count: number; total_duration_ms: number }> => {
    const { data } = await api.get('/tracks/liked/stats')
    return data
  },
  likedUnplayedStats: async (): Promise<{ count: number; total_duration_ms: number }> => {
    const { data } = await api.get('/tracks/liked-unplayed/stats')
    return data
  },
  recentlyAddedStats: async (): Promise<{ count: number; total_duration_ms: number }> => {
    const { data } = await api.get('/tracks/recently-added/stats')
    return data
  },
  getLikeStatus: async (id: string): Promise<{ liked: boolean | null }> => {
    const { data } = await api.get(`/tracks/${id}/like`)
    return data
  },
  setLike: async (id: string, liked: boolean | null): Promise<void> => {
    await api.post(`/tracks/${id}/like`, { liked })
  },
  getLyrics: async (id: string): Promise<LyricsResult> => {
    const { data } = await api.get(`/tracks/${id}/lyrics`)
    return data
  },
  recordPlay: async (id: string): Promise<void> => {
    await api.post(`/tracks/${id}/played`)
  },
  getPlayCount: async (id: string): Promise<{ play_count: number }> => {
    const { data } = await api.get(`/tracks/${id}/play-count`)
    return data
  },
  getPlayCountsBatch: async (ids: string[]): Promise<Record<string, number>> => {
    if (ids.length === 0) return {}
    const { data } = await api.get('/tracks/play-counts', { params: { ids: ids.join(',') } })
    return data
  },
  streamUrl: (id: string): string => {
    const token = localStorage.getItem('token')
    // Use transcode endpoint for browser-compatible audio (AAC)
    return `/api/stream/track/${id}/transcode?token=${token}`
  },
}

// Videos
export const videos = {
  listMovies: async (limit = 50, offset = 0): Promise<Video[]> => {
    const { data } = await api.get('/movies', { params: { limit, offset } })
    return data
  },
  get: async (id: string): Promise<Video> => {
    const { data } = await api.get(`/movies/${id}`)
    return data
  },
  updateMetadata: async (id: string, overview?: string, posterUrl?: string): Promise<Video> => {
    const { data } = await api.put(`/movies/${id}/metadata`, { overview, poster_url: posterUrl })
    return data
  },
  refreshMetadata: async (id: string): Promise<Video> => {
    const { data } = await api.post(`/movies/${id}/refresh`)
    return data
  },
  streamUrl: (id: string): string => {
    const token = localStorage.getItem('token')
    return `/api/stream/video/${id}?token=${token}`
  },
  getWatchedStatus: async (id: string): Promise<{ watched: boolean }> => {
    const { data } = await api.get(`/videos/${id}/watched`)
    return data
  },
  setWatched: async (id: string, watched: boolean): Promise<void> => {
    await api.post(`/videos/${id}/watched`, { watched })
  },
  getWatchedBatch: async (ids: string[]): Promise<Record<string, boolean>> => {
    if (ids.length === 0) return {}
    const { data } = await api.get('/videos/watched', { params: { ids: ids.join(',') } })
    return data
  },
}

// Series (TV Shows)
export const series = {
  list: async (limit = 50, offset = 0): Promise<SeriesWithEpisodes[]> => {
    const { data } = await api.get('/series', { params: { limit, offset } })
    return data
  },
  get: async (id: string): Promise<SeriesDetail> => {
    const { data } = await api.get(`/series/${id}`)
    return data
  },
  refresh: async (id: string): Promise<SeriesDetail> => {
    const { data } = await api.post(`/series/${id}/refresh`)
    return data
  },
}

// Search
export const search = async (query: string, limit = 20): Promise<SearchResult[]> => {
  const { data } = await api.get('/search', { params: { q: query, limit } })
  return data
}

export const rebuildSearchIndex = async (): Promise<{ success: boolean; message: string; count: number }> => {
  const { data } = await api.post('/search/rebuild-index')
  return data
}

// Discovery
export interface DiscoverySection {
  title: string
  based_on_artist: Artist | null
  recommended_artists: Artist[]
}

export const discovery = {
  get: async (): Promise<DiscoverySection[]> => {
    const { data } = await api.get('/discovery')
    return data
  },
}

// Playlists
export const playlists = {
  list: async (): Promise<Playlist[]> => {
    const { data } = await api.get('/playlists')
    return data
  },
  get: async (id: string): Promise<PlaylistWithTracks> => {
    const { data } = await api.get(`/playlists/${id}`)
    return data
  },
  create: async (name: string): Promise<Playlist> => {
    const { data } = await api.post('/playlists', { name })
    return data
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/playlists/${id}`)
  },
  addTrack: async (playlistId: string, trackId: string): Promise<void> => {
    await api.post(`/playlists/${playlistId}/tracks`, { track_id: trackId })
  },
  removeTrack: async (playlistId: string, trackId: string): Promise<void> => {
    await api.delete(`/playlists/${playlistId}/tracks/${trackId}`)
  },
}

// File Manager
export interface FileEntry {
  name: string
  path: string
  is_directory: boolean
  size: number | null
  modified: string | null
  extension: string | null
}

export interface DirectoryListing {
  path: string
  parent: string | null
  entries: FileEntry[]
  library_id: string
  library_name: string
}

export interface RenamePreview {
  original_path: string
  new_path: string
  item_type: string
}

export interface AutoRenamePreviewResponse {
  changes: RenamePreview[]
  error_count: number
}

export const files = {
  listDirectory: async (libraryId: string, path?: string): Promise<DirectoryListing> => {
    const { data } = await api.get('/files', { params: { library_id: libraryId, path } })
    return data
  },
  createFolder: async (libraryId: string, parentPath: string, name: string): Promise<void> => {
    await api.post('/files/folder', { library_id: libraryId, parent_path: parentPath, name })
  },
  rename: async (libraryId: string, path: string, newName: string): Promise<void> => {
    await api.post('/files/rename', { library_id: libraryId, path, new_name: newName })
  },
  delete: async (libraryId: string, path: string): Promise<void> => {
    await api.post('/files/delete', { library_id: libraryId, path })
  },
  move: async (libraryId: string, sourcePath: string, destPath: string): Promise<void> => {
    await api.post('/files/move', { library_id: libraryId, source_path: sourcePath, dest_path: destPath })
  },
  previewAutoRename: async (libraryId: string, path: string, recursive?: boolean): Promise<AutoRenamePreviewResponse> => {
    const { data } = await api.post('/files/auto-rename/preview', { library_id: libraryId, path, recursive })
    return data
  },
  applyAutoRename: async (libraryId: string, path: string, recursive?: boolean): Promise<{ applied: number; errors: number }> => {
    const { data } = await api.post('/files/auto-rename/apply', { library_id: libraryId, path, recursive })
    return data
  },
}

// User Management
export interface UserWithAccess {
  id: string
  username: string
  is_admin: boolean
  created_at: string
  library_ids: string[]
}

export const users = {
  list: async (): Promise<UserWithAccess[]> => {
    const { data } = await api.get('/users')
    return data
  },
  get: async (id: string): Promise<UserWithAccess> => {
    const { data } = await api.get(`/users/${id}`)
    return data
  },
  create: async (username: string, password: string, is_admin: boolean): Promise<UserWithAccess> => {
    const { data } = await api.post('/users', { username, password, is_admin })
    return data
  },
  update: async (id: string, updates: { username?: string; password?: string; is_admin?: boolean }): Promise<UserWithAccess> => {
    const { data } = await api.put(`/users/${id}`, updates)
    return data
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/users/${id}`)
  },
  getLibraryAccess: async (id: string): Promise<string[]> => {
    const { data } = await api.get(`/users/${id}/libraries`)
    return data
  },
  setLibraryAccess: async (id: string, libraryIds: string[]): Promise<UserWithAccess> => {
    const { data } = await api.put(`/users/${id}/libraries`, { library_ids: libraryIds })
    return data
  },
}

// System Status
export interface DiskInfo {
  name: string
  mount_point: string
  total_gb: number
  used_gb: number
  available_gb: number
  percent_used: number
}

export interface TranscodeStatus {
  id: number
  file_name: string
  quality: string
  elapsed_seconds: number
}

export interface SystemStatus {
  cpu_usage: number
  memory_used_gb: number
  memory_total_gb: number
  memory_percent: number
  disks: DiskInfo[]
  active_transcodes: number
  transcodes: TranscodeStatus[]
  os_name: string
  os_version: string
  hostname: string
  uptime_seconds: number
}

export const system = {
  getStatus: async (): Promise<SystemStatus> => {
    const { data } = await api.get('/status')
    return data
  },
}

// Global Download Settings (admin-configured, shared by all users)
export interface GlobalSettings {
  id: string
  spotify_client_id: string | null
  // DAB (dabmusic.xyz) settings
  dab_email: string | null
  dab_password: string | null
  dab_quality: number | null  // 5=MP3 320, 6=FLAC 16bit, 7=FLAC 24bit/44.1, 27=FLAC 24bit Hi-Res
  dab_enabled: number | null  // 1 = enabled, 0 = disabled
  // Soulseek (slskd) settings
  slskd_url: string | null
  slskd_api_key: string | null
  slskd_auto_fallback: number | null  // 1 = enabled, 0 = disabled
  slskd_download_path: string | null
  // Prowlarr settings
  prowlarr_url: string | null
  prowlarr_api_key: string | null
  prowlarr_username: string | null
  prowlarr_password: string | null
  // rTorrent settings
  rtorrent_url: string | null
  rtorrent_username: string | null
  rtorrent_password: string | null
  music_label: string | null
  // Rsync settings
  seedbox_host: string | null
  seedbox_path: string | null
  seedbox_ssh_key_path: string | null
  local_music_path: string | null
  naming_template: string | null
}

// Type alias for backward compatibility
export type UserDownloadSettings = GlobalSettings

export interface ConnectionStatus {
  success: boolean
  message: string
}

export interface TestConnectionResult {
  prowlarr: ConnectionStatus | null
  rtorrent: ConnectionStatus | null
  rsync: ConnectionStatus | null
}

export interface ImportJob {
  id: string
  user_id: string
  source_type: string
  source_url: string
  playlist_name: string | null
  status: string
  total_tracks: number
  unique_albums: number
  albums_found: number
  albums_downloaded: number
  albums_synced: number
  tracks_matched: number
  created_playlist_id: string | null
  error_message: string | null
  created_at: string
  updated_at: string
}

export interface ImportJobAlbum {
  id: string
  job_id: string
  artist_name: string
  album_name: string
  track_count: number
  status: string
  search_type: string | null
  prowlarr_results: ProwlarrSearchResult[] | null
  selected_result_guid: string | null
  selected_result_title: string | null
  selected_result_indexer_id: number | null
  torrent_hash: string | null
  download_progress: number
  synced_path: string | null
  matched_album_id: string | null
  error_message: string | null
  created_at: string
}

export interface ImportJobTrack {
  id: string
  job_id: string
  album_job_id: string | null
  artist_name: string
  track_name: string
  album_name: string | null
  track_number: number | null
  status: string
  matched_track_id: string | null
  error_message: string | null
  created_at: string
}

export interface ProwlarrSearchResult {
  guid: string
  title: string
  size: number
  seeders: number
  indexer_id: number
  indexer_name: string
  download_url: string | null
}

export interface ImportJobLog {
  id: string
  job_id: string
  level: string
  message: string
  details: string | null
  created_at: string
}

export interface ImportJobWithDetails extends ImportJob {
  albums: ImportJobAlbum[]
  tracks: ImportJobTrack[]
  logs: ImportJobLog[]
}

// Download Settings
export const downloadSettings = {
  get: async (): Promise<UserDownloadSettings | null> => {
    const { data } = await api.get('/download-settings')
    // The backend uses #[serde(flatten)], so settings are at the top level
    // If id exists, we have settings; otherwise null
    return data?.id ? data : null
  },
  update: async (settings: Partial<UserDownloadSettings>): Promise<UserDownloadSettings> => {
    // Backend expects boolean for slskd_auto_fallback and dab_enabled, but we store as number
    const payload = {
      ...settings,
      slskd_auto_fallback: settings.slskd_auto_fallback !== undefined
        ? settings.slskd_auto_fallback === 1
        : undefined,
      dab_enabled: settings.dab_enabled !== undefined
        ? settings.dab_enabled === 1
        : undefined
    }
    const { data } = await api.put('/download-settings', payload)
    return data
  },
  test: async (): Promise<TestConnectionResult> => {
    const { data } = await api.post('/download-settings/test')
    return data
  },
  testSlskd: async (): Promise<ConnectionStatus> => {
    const { data } = await api.post('/download-settings/test-slskd')
    return data
  },
  testDab: async (): Promise<ConnectionStatus> => {
    const { data } = await api.post('/download-settings/test-dab')
    return data
  },
}

// Spotify Importer
export const importer = {
  importSpotify: async (url: string): Promise<ImportJob> => {
    const { data } = await api.post('/import/spotify', { url })
    return data
  },
  importCsv: async (file: File, name?: string): Promise<ImportJob> => {
    const formData = new FormData()
    formData.append('file', file)
    if (name) {
      formData.append('name', name)
    }
    const { data } = await api.post('/import/csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return data
  },
  importTracks: async (
    playlistName: string,
    tracks: Array<{ artist_name: string; track_name: string; album_name: string }>,
    likeTracks = false
  ): Promise<ImportJob> => {
    const { data } = await api.post('/import/tracks', {
      playlist_name: playlistName,
      tracks,
      like_tracks: likeTracks,
    })
    return data
  },
  listJobs: async (): Promise<ImportJobWithDetails[]> => {
    const { data } = await api.get('/import/jobs')
    return data
  },
  getJob: async (id: string): Promise<ImportJobWithDetails> => {
    const { data } = await api.get(`/import/jobs/${id}`)
    return data
  },
  deleteJob: async (id: string): Promise<void> => {
    await api.delete(`/import/jobs/${id}`)
  },
  searchAlbum: async (jobId: string, albumId: string): Promise<ProwlarrSearchResult[]> => {
    const { data } = await api.get(`/import/jobs/${jobId}/albums/${albumId}/search`)
    return data
  },
  selectResult: async (jobId: string, albumId: string, guid: string, indexerId: number): Promise<void> => {
    await api.post(`/import/jobs/${jobId}/albums/${albumId}/select`, { guid, indexer_id: indexerId })
  },
  skipAlbum: async (jobId: string, albumId: string): Promise<void> => {
    await api.post(`/import/jobs/${jobId}/albums/${albumId}/skip`)
  },
  retryAlbum: async (jobId: string, albumId: string): Promise<void> => {
    await api.post(`/import/jobs/${jobId}/albums/${albumId}/retry`)
  },
  stopAlbum: async (jobId: string, albumId: string): Promise<void> => {
    await api.post(`/import/jobs/${jobId}/albums/${albumId}/stop`)
  },
  // Job control
  pauseJob: async (jobId: string): Promise<void> => {
    await api.post(`/import/jobs/${jobId}/pause`)
  },
  resumeJob: async (jobId: string): Promise<void> => {
    await api.post(`/import/jobs/${jobId}/resume`)
  },
  cancelJob: async (jobId: string): Promise<void> => {
    await api.post(`/import/jobs/${jobId}/cancel`)
  },
  restartJob: async (jobId: string): Promise<void> => {
    await api.post(`/import/jobs/${jobId}/restart`)
  },
  getJobLogs: async (jobId: string): Promise<ImportJobLog[]> => {
    const { data } = await api.get(`/import/jobs/${jobId}/logs`)
    return data
  },
  // Discography and scanning
  searchDiscography: async (jobId: string, artistName: string): Promise<ProwlarrSearchResult[]> => {
    const { data } = await api.get(`/import/jobs/${jobId}/discography/${encodeURIComponent(artistName)}`)
    return data
  },
  downloadDiscography: async (jobId: string, guid: string, indexerId: number, artistName: string): Promise<void> => {
    await api.post(`/import/jobs/${jobId}/discography`, { guid, indexer_id: indexerId, artist_name: artistName })
  },
  scanDownloaded: async (jobId: string): Promise<{ matched: number; total_folders: number; matches: Array<{ album_id: string; artist_name: string; album_name: string; matched_folder: string; status: string }> }> => {
    const { data } = await api.post(`/import/jobs/${jobId}/scan`)
    return data
  },
  syncDownloaded: async (jobId: string): Promise<{ message: string; albums_to_sync: number }> => {
    const { data } = await api.post(`/import/jobs/${jobId}/sync`)
    return data
  },
  markAlbum: async (jobId: string, albumId: string, status: string): Promise<void> => {
    await api.post(`/import/jobs/${jobId}/albums/${albumId}/mark`, { status })
  },
  createPlaylistFromImport: async (jobId: string, playlistName?: string, playlistId?: string, likeTracks?: boolean): Promise<{
    playlist_id: string
    playlist_name: string
    total_tracks: number
    matched_tracks: number
    pending_tracks: number
    liked_tracks: number
  }> => {
    const { data } = await api.post(`/import/jobs/${jobId}/playlist`, {
      playlist_name: playlistName,
      playlist_id: playlistId,
      like_tracks: likeTracks,
    })
    return data
  },
  likeMatchedTracks: async (jobId: string): Promise<{
    liked_count: number
    already_liked: number
    total_matched: number
  }> => {
    // Long timeout for processing many tracks
    const { data } = await api.post(`/import/jobs/${jobId}/like-matched`, {}, { timeout: 300000 })
    return data
  },
  // DAB (dabmusic.xyz) integration
  searchAlbumDab: async (jobId: string, albumId: string, query?: string): Promise<DabSearchResult[]> => {
    const params = query ? `?query=${encodeURIComponent(query)}` : ''
    const { data } = await api.get(`/import/jobs/${jobId}/albums/${albumId}/search-dab${params}`)
    return data
  },
  selectDabResult: async (jobId: string, albumId: string, dabAlbumId: string): Promise<void> => {
    await api.post(`/import/jobs/${jobId}/albums/${albumId}/select-dab`, { album_id: dabAlbumId })
  },
  // YouTube (yt-dlp) integration
  searchYoutube: async (query: string, maxResults = 5): Promise<YtSearchResult[]> => {
    const { data } = await api.get('/import/search-youtube', {
      params: { query, max_results: maxResults }
    })
    return data
  },
  downloadYoutube: async (
    jobId: string,
    albumId: string,
    url: string,
    artist: string,
    album: string,
    title: string,
    trackNumber?: number,
    convertToFlac = true
  ): Promise<YtDownloadResult> => {
    const { data } = await api.post(`/import/jobs/${jobId}/albums/${albumId}/download-youtube`, {
      url,
      artist,
      album,
      title,
      track_number: trackNumber,
      convert_to_flac: convertToFlac
    })
    return data
  },
  checkYtdlpStatus: async (): Promise<YtdlpStatus> => {
    const { data } = await api.get('/download-settings/test-ytdlp')
    return data
  },
  // Standalone DAB search (for Discover page)
  searchDab: async (query: string): Promise<DabSearchResult[]> => {
    const { data } = await api.get('/search/dab', { params: { query } })
    return data
  },
  // Standalone YouTube download (for Discover page)
  downloadYoutubeStandalone: async (
    url: string,
    artist: string,
    title: string,
    album?: string,
    convertToFlac = true
  ): Promise<YtDownloadResult> => {
    const { data } = await api.post('/download/youtube', {
      url,
      artist,
      title,
      album,
      convert_to_flac: convertToFlac
    })
    return data
  },
  // Standalone DAB download (for Discover page)
  downloadDabStandalone: async (
    albumId: string,
    artist: string,
    albumTitle: string,
    quality?: number
  ): Promise<DabDownloadResult> => {
    const { data } = await api.post('/download/dab', {
      album_id: albumId,
      artist,
      album_title: albumTitle,
      quality
    })
    return data
  },
  // Standalone Monochrome search/download (for Discover page)
  searchMonochrome: async (query: string): Promise<HifiSearchResult[]> => {
    const { data } = await api.get('/search/monochrome', { params: { query } })
    return data
  },
  downloadMonochromeStandalone: async (
    albumId: string,
    artist: string,
    albumTitle: string,
    quality?: string
  ): Promise<HifiDownloadResult> => {
    const { data } = await api.post('/download/monochrome', {
      album_id: parseInt(albumId),
      artist,
      album_title: albumTitle,
      quality
    })
    return data
  },
  // Standalone Squid search/download (for Discover page)
  searchSquid: async (query: string): Promise<HifiSearchResult[]> => {
    const { data } = await api.get('/search/squid', { params: { query } })
    return data
  },
  downloadSquidStandalone: async (
    albumId: string,
    artist: string,
    albumTitle: string,
    quality?: string
  ): Promise<HifiDownloadResult> => {
    const { data } = await api.post('/download/squid', {
      album_id: parseInt(albumId),
      artist,
      album_title: albumTitle,
      quality
    })
    return data
  },
  // Prowlarr video search (Movies)
  searchProwlarrMovies: async (query: string): Promise<ProwlarrVideoResult[]> => {
    const { data } = await api.get('/search/prowlarr/movies', { params: { q: query } })
    return data
  },
  // Prowlarr video search (TV)
  searchProwlarrTv: async (query: string): Promise<ProwlarrVideoResult[]> => {
    const { data } = await api.get('/search/prowlarr/tv', { params: { q: query } })
    return data
  },
  // Prowlarr download (adds to rTorrent with label)
  downloadProwlarr: async (
    guid: string,
    indexerId: number,
    title: string,
    mediaType: 'movie' | 'tv',
    downloadUrl: string | null,
    magnetUrl: string | null
  ): Promise<ProwlarrDownloadResult> => {
    const { data } = await api.post('/download/prowlarr', {
      guid,
      indexer_id: indexerId,
      title,
      media_type: mediaType,
      download_url: downloadUrl,
      magnet_url: magnetUrl
    })
    return data
  },
  // Video sync (seedbox to local library)
  getVideoSyncStatus: async (): Promise<VideoSyncStatus[]> => {
    const { data } = await api.get('/sync/video/status')
    return data
  },
  syncVideoTorrents: async (label: 'movies' | 'tv', hash?: string): Promise<SyncVideoResult> => {
    const { data } = await api.post('/sync/video', { label, hash })
    return data
  },
}

// Prowlarr Video Search Result type (used for Movies/TV search)
export interface ProwlarrVideoResult {
  guid: string
  title: string
  size: number
  seeders: number
  indexer_id: number
  indexer_name: string
  download_url: string | null
  magnet_url: string | null
}

// Prowlarr download result
export interface ProwlarrDownloadResult {
  success: boolean
  message: string
}

// Video sync types
export interface CompletedTorrent {
  hash: string
  name: string
  remote_path: string
  is_synced: boolean
}

export interface VideoSyncStatus {
  label: string
  completed: CompletedTorrent[]
  syncing: SyncingTorrent[]
  synced: string[]
}

export interface SyncingTorrent {
  hash: string
  name: string
  progress: number
}

export interface SyncDetail {
  name: string
  success: boolean
  local_path: string | null
  error: string | null
}

export interface SyncVideoResult {
  synced_count: number
  failed_count: number
  details: SyncDetail[]
}

// DAB Search Result type
export interface DabSearchResult {
  album_id: string
  title: string
  artist: string
  year: string | null
  genre: string | null
  quality: string
  track_count: number
  cover_url: string | null
  track_titles: string[]
  // Additional metadata for display
  release_date: string | null
  bit_depth: number
  sample_rate: number
  is_hi_res: boolean
  label: string | null
  duration: number | null
}

// DAB Download Result type
export interface DabDownloadResult {
  success: boolean
  message: string
  files_count: number
  path: string
  album_id: string | null
}

// HiFi Search Result type (same shape as DabSearchResult)
export type HifiSearchResult = DabSearchResult

// HiFi Download Result type
export interface HifiDownloadResult {
  success: boolean
  message: string
  files_count: number
  path: string
}

// YouTube search result
export interface YtSearchResult {
  id: string
  title: string
  channel: string
  duration: string
  url: string
  thumbnail: string | null
}

export interface YoutubeVideoDetails {
  id: string
  title: string
  channel: string
  duration_seconds: number | null
  description: string | null
  thumbnail: string | null
  webpage_url: string
}

export interface YoutubeFeedItem extends YtSearchResult {
  watched: boolean
  position_seconds: number
}

export interface YoutubeConnectionStatus {
  connected: boolean
  connected_at: string | null
  account_label: string | null
  account_avatar: string | null
}

export interface YoutubePlaybackState {
  id: string
  user_id: string
  video_id: string
  title: string | null
  channel: string | null
  thumbnail: string | null
  position_seconds: number
  duration_seconds: number | null
  watched: boolean
  updated_at: string
}

export const youtube = {
  search: async (query: string, maxResults = 18): Promise<YtSearchResult[]> => {
    const { data } = await api.get('/youtube/search', {
      params: { query, max_results: maxResults },
    })
    return data
  },
  feed: async (maxResults = 24, kind: 'home' | 'subscriptions' = 'home'): Promise<YoutubeFeedItem[]> => {
    const { data } = await api.get('/youtube/feed', { params: { max_results: maxResults, kind } })
    return data
  },
  history: async (): Promise<YoutubePlaybackState[]> => {
    const { data } = await api.get('/youtube/history')
    return data
  },
  connection: async (): Promise<YoutubeConnectionStatus> => {
    const { data } = await api.get('/youtube/connection')
    return data
  },
  connect: async (cookies: string): Promise<YoutubeConnectionStatus> => {
    const { data } = await api.post('/youtube/connection', { cookies })
    return data
  },
  disconnect: async (): Promise<void> => {
    await api.delete('/youtube/connection')
  },
  getVideo: async (id: string): Promise<YoutubeVideoDetails> => {
    const { data } = await api.get(`/youtube/videos/${id}`)
    return data
  },
  getPlayback: async (id: string): Promise<YoutubePlaybackState | null> => {
    const { data } = await api.get(`/youtube/videos/${id}/playback`)
    return data
  },
  savePlayback: async (
    id: string,
    positionSeconds: number,
    durationSeconds: number | null,
    watched = false,
    metadata?: { title?: string; channel?: string; thumbnail?: string | null },
  ): Promise<void> => {
    await api.put(`/youtube/videos/${id}/playback`, {
      position_seconds: positionSeconds,
      duration_seconds: durationSeconds,
      watched,
      title: metadata?.title,
      channel: metadata?.channel,
      thumbnail: metadata?.thumbnail,
    })
  },
  streamUrl: (id: string): string => {
    const token = localStorage.getItem('token') || ''
    return `/api/youtube/videos/${id}/stream?token=${encodeURIComponent(token)}`
  },
}

// YouTube download result
export interface YtDownloadResult {
  success: boolean
  file_path: string | null
  error: string | null
}

// YouTube/yt-dlp status
export interface YtdlpStatus {
  ytdlp_available: boolean
  ffmpeg_available: boolean
}

// Player state persistence
export interface PlayerStateData {
  current_track_id: string | null
  queue_index: number
  position_ms: number
  shuffle_enabled: boolean
  repeat_mode: 'off' | 'one' | 'all'
  queue: string[] // Track IDs
}

export const playerState = {
  get: async (): Promise<PlayerStateData | null> => {
    const { data } = await api.get('/player/state')
    return data
  },
  save: async (state: PlayerStateData): Promise<void> => {
    await api.put('/player/state', state)
  },
  clear: async (): Promise<void> => {
    await api.delete('/player/state')
  },
}

export interface RemotePlayerStatus {
  online: boolean
  playing?: string | null
  subtitle?: string | null
  media_type?: string | null
  media_id?: string | null
  paused: boolean
  position_seconds?: number | null
  duration_seconds?: number | null
  volume?: number | null
}

export interface RemotePlayer {
  player_id: string
  name: string
  enabled: boolean
  last_seen_at: string
  status: RemotePlayerStatus
}

export interface RemotePlayerCommandRequest {
  kind: 'play' | 'pause' | 'resume' | 'toggle_pause' | 'stop' | 'seek' | 'volume'
  media_type?: 'track' | 'movie' | 'episode' | 'youtube'
  media_id?: string
  title?: string
  position_seconds?: number
  volume?: number
}

export const remotePlayers = {
  list: async (): Promise<RemotePlayer[]> => {
    const { data } = await api.get('/remote-players')
    return data
  },
  command: async (playerId: string, command: RemotePlayerCommandRequest): Promise<void> => {
    await api.post(`/remote-players/${playerId}/commands`, command)
  },
}

// Android APK info
export interface ApkInfoResponse {
  version_name: string
  version_code: number
  build_date: string
  file_size_mb: number
  min_sdk_version: number
  changelog: string
  download_url: string
}

export const android = {
  getInfo: async (): Promise<ApkInfoResponse> => {
    const { data } = await api.get('/android/info')
    return data
  },
  getDownloadUrl: (): string => {
    const token = localStorage.getItem('token')
    return `/api/android/download?token=${token}`
  },
}

// ============================================================================
// Track Sharing
// ============================================================================

export interface ShareResponse {
  id: string
  token: string
  share_url: string
  expires_at: string | null
  max_plays: number | null
}

export interface SharedTrackInfo {
  token: string
  track: TrackWithDetails
  shared_by_username: string
  expires_at: string | null
  play_count: number
  max_plays: number | null
}

export interface CreateShareRequest {
  expires_in_hours?: number | null
  max_plays?: number | null
}

export const shares = {
  create: async (trackId: string, options?: CreateShareRequest): Promise<ShareResponse> => {
    const { data } = await api.post(`/tracks/${trackId}/share`, options || {})
    return data
  },
  list: async (): Promise<ShareResponse[]> => {
    const { data } = await api.get('/shares')
    return data
  },
  delete: async (shareId: string): Promise<void> => {
    await api.delete(`/shares/${shareId}`)
  },
  // Public endpoints (no auth required)
  getSharedTrack: async (token: string): Promise<SharedTrackInfo> => {
    const { data } = await axios.get(`/api/shared/${token}`)
    return data
  },
  getStreamUrl: (token: string, transcode = true): string => {
    return transcode
      ? `/api/stream/shared/${token}/transcode`
      : `/api/stream/shared/${token}`
  },
}

// ============================================================================
// Playlist Sharing
// ============================================================================

export interface PlaylistShareResponse {
  id: string
  token: string
  share_url: string
  expires_at: string | null
}

export interface SharedPlaylistShare {
  id: string
  playlist_id: string
  shared_by: string
  token: string
  expires_at: string | null
  created_at: string
  last_accessed_at: string | null
}

export interface SharedPlaylistInfo {
  token: string
  playlist: {
    id: string
    name: string
    created_at: string
    tracks: TrackWithDetails[]
  }
  shared_by_username: string
  expires_at: string | null
}

export interface CreatePlaylistShareRequest {
  expires_in_hours?: number | null
}

export const playlistShares = {
  create: async (playlistId: string, options?: CreatePlaylistShareRequest): Promise<PlaylistShareResponse> => {
    const { data } = await api.post(`/playlists/${playlistId}/share`, options || {})
    return data
  },
  delete: async (shareId: string): Promise<void> => {
    await api.delete(`/shares/playlist/${shareId}`)
  },
  listMyShares: async (): Promise<SharedPlaylistShare[]> => {
    const { data } = await api.get('/shares/playlists')
    return data
  },
  setVisibility: async (playlistId: string, visibility: 'private' | 'internal'): Promise<void> => {
    await api.put(`/playlists/${playlistId}/visibility`, { visibility })
  },
  listSharedWithMe: async (): Promise<Playlist[]> => {
    const { data } = await api.get('/playlists/shared')
    return data
  },
  // Public endpoints (no auth required)
  getSharedPlaylist: async (token: string): Promise<SharedPlaylistInfo> => {
    const { data } = await axios.get(`/api/shared/playlist/${token}`)
    return data
  },
  getStreamUrl: (token: string, trackId: string): string => {
    return `/api/stream/shared/playlist/${token}/${trackId}`
  },
}

// Re-export types for convenience
export type { Track, TrackWithDetails, Playlist, PlaylistVisibility } from '../types'

export default api
